/**
 * Menu latéral de la console : une entrée par section (`config/sections`),
 * filtrée par rôle. Le détail d'une section se parcourt par ses onglets, il
 * n'y a donc plus de sous-menus à déplier.
 */
import React from 'react';
import {
  Badge, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRBAC } from '../../contexts/RBACContext';
import { SECTIONS } from '../../config/sections';
import { MENU_LATERAL } from '../../theme/theme';
import { sectionVisible } from '../navigation/ongletsVisibles';
// Le hook seul, pas l'index : les écrans de modération restent chargés à la demande.
import { useNombreATraiter } from '../../features/moderation/hooks/useATraiter';

/** Section dont l'entrée porte le nombre d'éléments à traiter. */
const CLE_MODERATION = 'moderation';

/** Entrée du menu sur fond vert profond : survol discret, section active en vert vif. */
const entreeSx = {
  mx: 1, my: 0.25, borderRadius: 2, color: MENU_LATERAL.texte,
  '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 40 },
  '&:hover': { backgroundColor: MENU_LATERAL.survol },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: MENU_LATERAL.fondActif, color: MENU_LATERAL.texteActif, fontWeight: 600,
  },
} as const;

/** Vrai si l'adresse courante appartient à la section. */
const estDansLaSection = (adresse: string, chemin: string): boolean =>
  adresse === chemin || adresse.startsWith(`${chemin}/`);

/** Titre « Suklu Academy » et les sections ouvertes au compte. */
const SideMenu: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { hasRole } = useRBAC();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const moderation = SECTIONS.find((section) => section.cle === CLE_MODERATION);
  const nombreATraiter = useNombreATraiter(!!moderation && sectionVisible(moderation, hasRole));
  const ouvrir = (chemin: string) => {
    navigate(chemin);
    onNavigate?.();
  };

  return (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: MENU_LATERAL.texteActif, fontWeight: 800 }}>
          Suklu Admin
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: MENU_LATERAL.survol }} />
      <List>
        {SECTIONS.filter((section) => sectionVisible(section, hasRole)).map((section) => (
          <ListItem key={section.cle} disablePadding>
            <ListItemButton
              selected={estDansLaSection(pathname, section.chemin)}
              onClick={() => ouvrir(section.chemin)}
              sx={entreeSx}
            >
              <ListItemIcon>{section.icone}</ListItemIcon>
              <ListItemText primary={section.titre} />
              {section.cle === CLE_MODERATION && nombreATraiter > 0 && (
                <Badge badgeContent={nombreATraiter} color="error" max={99} sx={{ mr: 1.5 }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );
};

export default SideMenu;
